import React, { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { DateTime } from 'luxon'
import * as XLSXStyle from 'xlsx-js-style'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'

import { ImageList, ImageListItem } from '@material-ui/core'
import PrintButton from '../../../components/print-button/print-button.component'
import ReportInputs from '../../../components/report-inputs/report-inputs.component'
import SimpleBackdrop from '../../../components/simple-backdrop/simple-backdrop.component'
import AppInsightsTrackingContext from '../../../context/app-insights-tracking/AppInsightsTrackingContext'

import PdfMakerService from '../../../services/pdf-maker/pdf-maker-service'
import RequestService from '../../../services/request/request-service'

import makeStyles from '../reports.styles'
import { numberToPercentage } from '../../../shared/utils/utils'

import { BRIX_SUMMARY_QUERY } from '../../../shared/constants/queries'

const Quality = ({
  selectedLot,
  selectedLotName,
  selectedVariety,
  selectedVarietyName,
  currentFacility,
  selectedStartDate,
  selectedEndDate,
  desTemplates,
  userEmail,
}) => {
  const classes = makeStyles()
  const history = useHistory()
  const { t } = useTranslation()

  const [showReport, setShowReport] = useState(false)
  const [showBackdrop, setShowBackdrop] = useState(false)
  const [reportDate] = useState(DateTime.now().toFormat('LL-dd-yy'))

  const [generalData, setGeneralData] = useState({})

  const [sizeData, setSizeData] = useState([])
  const { trackEvent } = useContext(AppInsightsTrackingContext)

  useEffect(() => {
    if (selectedVariety === 0 || selectedLot === 0) {
      setShowReport(false)
    }
  }, [selectedLot, selectedVariety])

  useEffect(() => {
    return () => {
      setGeneralData({})
      setSizeData([])
    }
  }, [])

  const loadQualityReport = async () => {
    try {
      setShowBackdrop(true)
      // eslint-disable-next-line max-len
      const url = `${BRIX_SUMMARY_QUERY}?id_lot=${selectedLot}&id_variety=${selectedVariety}&id_facility=${currentFacility.id}&start_date=${selectedStartDate}Z&end_date=${selectedEndDate}Z`
      const response = await RequestService.Get(url, history)
      const {
        id_variety,
        variety,
        id_lot,
        lot_number,
        size_data,
        total_size_distribution,
        total_average_brix,
        total_average_firmness,
        total_sample_count,
      } = response.data

      setGeneralData({
        general: {
          id_variety,
          variety,
          id_lot,
          lotNumber: lot_number,
          reportDate,
          // totalSizeDistribution: numberToPercentage(((total_size_distribution ?? 0) * 100).toFixed(2)),
          totalSizeDistribution: numberToPercentage(total_size_distribution),
          totalAverageBrix: total_average_brix.toFixed(2),
          totalAverageFirmness: total_average_firmness.toFixed(2),
          totalSampleCount: total_sample_count,
        },
      })
      setSizeData(size_data)
      setShowBackdrop(false)
      setShowReport(true)
      trackEvent('FILTE_BUTTON_CLICK', { email: userEmail })
    } catch (error) {
      setShowBackdrop(false)
      console.log(error)
    }
  }

  const printDocument = () => {
    if (!generalData?.general) return
    trackEvent('PRINT_REPORT_BRIX_SUMMARY_CLICK', { email: userEmail })
    PdfMakerService.generateReport(
      `BrixSummary_${generalData?.general?.lotNumber?.trim() ?? ''}_${generalData?.general?.variety?.trim() ?? ''}`,
      'BrixSummary',
      {
        ...generalData,
        sizeData,
      }
    )
  }

  const printfReportExcel = () => {
    trackEvent('EXPORT_EXCEL_REPORT_CLICK', { email: userEmail })
    const sizeDataExcel = sizeData?.map((size) => {
      return [
        {
          t: 's',
          s: { font: { sz: 11 } },
          v: size.size_description,
        },
        {
          t: 's',
          s: { font: { sz: 11 } },
          v: `${size.size_distribution.toFixed(2)}%`,
        },
        {
          t: 's',
          s: { font: { sz: 11 } },
          v: size.average_brix.toFixed(2),
        },
        {
          t: 's',
          s: { font: { sz: 11 } },
          v: size.average_firmness.toFixed(2),
        },
      ]
    })
    const sizeDistributionData = [
      [
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: 'Lot #:',
        },
        {
          t: 's',
          s: { font: { sz: 11 } },
          v: generalData.general?.id_lot !== 0 ? generalData.general?.lotNumber : 'All',
        },
      ],
      [
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: 'Variety:',
        },
        {
          t: 's',
          s: { font: { sz: 11 } },
          v: generalData.general?.id_variety !== 0 ? generalData.general?.variety : 'All',
        },
      ],
      [
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: 'Size',
        },
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: 'Size Distribution',
        },
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: 'Average Brix',
        },
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: 'Average Firmness',
        },
      ],
      ...sizeDataExcel,
      [
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: 'Totals',
        },
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: `${generalData.general.totalSizeDistribution}`,
        },
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: `${generalData.general.totalAverageBrix}`,
        },
        {
          t: 's',
          s: { font: { bold: true, sz: 11 } },
          v: `${generalData.general.totalAverageFirmness}`,
        },
      ],
    ]

    const sizeDistribution = XLSXStyle.utils.aoa_to_sheet(sizeDistributionData)
    const sizeDistributionCols = [{ wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 30 }]
    sizeDistribution['!cols'] = sizeDistributionCols
    const excelFile = XLSXStyle.utils.book_new()
    XLSXStyle.utils.book_append_sheet(excelFile, sizeDistribution, 'Brix Summary')
    XLSXStyle.writeFile(
      excelFile,
      `BrixSummary_${generalData?.general?.lotNumber?.trim() ?? ''}_${generalData?.general?.variety?.trim() ?? ''}.xlsx`
    )
  }

  return (
    <Grid container>
      {!showReport ? null : <PrintButton printDocument={printDocument} printfReportExcel={printfReportExcel} />}
      <ReportInputs loadData={loadQualityReport} isLoading={showBackdrop} />
      {!showReport ? null : (
        <>
          <Grid xs={12} item>
            <Typography align="center" variant="h6">
              {t('brix')} Summary
            </Typography>
          </Grid>
          <Grid xs={12} item>
            <p className={`${classes.tableCellLabel}`}>
              {t('lot')} #: {generalData.general?.id_lot !== 0 ? generalData.general?.lotNumber : 'All'}
            </p>
            <p className={`${classes.tableCellLabel}`}>
              {t('variety')} : {generalData.general?.id_variety !== 0 ? generalData.general?.variety : 'All'}
            </p>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow key="sizeData">
                    <TableCell className={`${classes.tableCell} ${classes.tableCellLabel}`}>{t('size')}</TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.tableCellLabel}`}>
                      {t('size_distribution')}
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.tableCellLabel}`}>
                      {t('average_brix')}
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.tableCellLabel}`}>
                      {t('average_firmness')}
                    </TableCell>
                  </TableRow>

                  {sizeData.map((size, index) => (
                    <TableRow key={index}>
                      <TableCell className={`${classes.tableCell} ${classes.sizeColumn}`}>
                        {size.size_description}
                      </TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.distributionColumn}`}>
                        {size.size_distribution.toFixed(2)}%
                      </TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.brixFirmnessColumn}`}>
                        {size.average_brix.toFixed(2)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>{size.average_firmness.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow key="sizeDataTotal">
                    <TableCell className={`${classes.tableCell} ${classes.tableCellLabel}`}>Totals</TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.tableCellLabel}`}>
                      {generalData.general.totalSizeDistribution}
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.tableCellLabel}`}>
                      {generalData.general.totalAverageBrix}
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.tableCellLabel}`}>
                      {generalData.general.totalAverageFirmness}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </>
      )}
      <SimpleBackdrop open={showBackdrop} />
    </Grid>
  )
}

const mapStateToProps = (state) => ({
  selectedLot: state.reports.selectedLot,
  selectedLotName: state.reports.selectedLotName,
  selectedVariety: state.reports.selectedVariety,
  selectedVarietyName: state.reports.selectedVarietyName,
  selectedStartDate: state.reports.selectedStartDate,
  selectedEndDate: state.reports.selectedEndDate,
  currentFacility: state.facility.currentFacility,
  userEmail: state.user.email,
})

export default connect(mapStateToProps)(Quality)
